import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import App from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { SnackbarProvider } from "notistack";
import { useEffect, useRef, useState } from "react";

import "@fontsource/work-sans/300.css";
import "@fontsource/work-sans/400.css";
import "@fontsource/work-sans/500.css";
import "@fontsource/work-sans/600.css";
import "@fontsource/work-sans/700.css";

import AccessDeniedContainer from "@common/AccessDeniedContainer";
import HomePage from "@common/home/HomePage";
import ShopNotFoundContainer from "@common/ShopNotFoundContainer";
import { setShopTheme } from "@common/utils/utils";
import { API_VERSION, BACKEND_URL } from "@config/constants";
import createEmotionCache from "@config/createEmotionCache";
import GeneralShopMeta from "@config/meta/GeneralShopMeta";
import { GeolocationProvider } from "@hooks/useGeolocation";
import { ShopProvider } from "@hooks/useShopContext";
import baseTheme from "../styles/baseTheme";

// TODO: fb pixel related
import { ShopNavProvider } from "@hooks/useShopNav";

const clientSideEmotionCache = createEmotionCache();

const MyApp = (props) => {
  const router = useRouter();
  const {
    Component,
    pageProps,
    shop,
    validSubdomain,
    emotionCache = clientSideEmotionCache,
  } = props;

  const [notFound, setNotFound] = useState(false);
  const [shopClosed, setShopClosed] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  /**
   * TODO: Resolve className did not match error
   * Pending resolution on mui; https://github.com/mui/material-ui/issues/27512
   */
  const [loaded, setLoaded] = useState(false);

  // add action to all snackbars
  const notistackRef = useRef();
  // eslint-disable-next-line no-unused-vars
  const onClickDismiss = (key) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  let theme = baseTheme;
  if (validSubdomain === true) {
    theme = setShopTheme(shop || JSON.parse(localStorage.getItem("shop")), baseTheme);
  }

  const getShop = () => {
    if (!validSubdomain) {
      return;
    }
    return shop || JSON.parse(localStorage.getItem("shop"));
  };

  const getGeolocation = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/get-addr-info`);
      const updatedGeolocation = await res.json();

      if (res.status === 200) {
        localStorage.setItem("geolocation", JSON.stringify(updatedGeolocation));
      }
    } catch (err) {
      /* empty */
    }
  };

  //
  useEffect(() => {
    if (shop && validSubdomain === true) {
      delete shop.vendor.user;
      delete shop.vendor.bank_name;
      delete shop.vendor.bank_account_number;
      localStorage.setItem("shop", JSON.stringify(shop));
    }

    if (shop && !shop.is_active) {
      setShopClosed(true);
    }

    if (!localStorage.getItem("shop") || validSubdomain === false) {
      setNotFound(true);
    }

    if (shop) {
      getGeolocation().then(() => {
        if (
          shop.geo_blocked &&
          shop.restricted_countries.includes(
            JSON.parse(localStorage.getItem("geolocation"))?.country_iso_code,
          )
        ) {
          setAccessDenied(true);
        }
        setLoaded(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (notFound) {
    return (
      <CacheProvider value={emotionCache}>
        <Head>
          <title>Shop Not Found | Powered by Carte</title>
          <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
          <meta name="robots" content="index, follow" />
        </Head>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ShopNotFoundContainer />
          </ThemeProvider>
        </StyledEngineProvider>
      </CacheProvider>
    );
  }

  // to restrict customers from entering shop based on countries
  if (accessDenied) {
    return (
      <CacheProvider value={emotionCache}>
        <GeneralShopMeta shop={shop} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <ShopProvider shop={shop}>
              <CssBaseline />
              {/* <SnowParticles /> */}
              <AccessDeniedContainer />
            </ShopProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </CacheProvider>
    );
  }

  // currently hardcoded /review path allowed when shop is closed
  if (shopClosed && !router.asPath.includes("/review")) {
    return (
      <CacheProvider value={emotionCache}>
        <GeneralShopMeta shop={shop} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <ShopProvider shop={shop}>
              <CssBaseline />
              {/* <SnowParticles /> */}
              <HomePage shop={shop} />
            </ShopProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </CacheProvider>
    );
  }

  return (
    <CacheProvider value={emotionCache}>
      <GeneralShopMeta shop={shop} />

      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <GeolocationProvider>
            <ShopProvider shop={shop}>
              <ShopNavProvider>
                <SnackbarProvider
                  sx={{
                    "& .SnackbarContent-root": {
                      backgroundColor: theme.palette.accent.main,
                      color: theme.palette.accent.contrastText,
                    },
                  }}
                  maxSnack={2}
                  ref={notistackRef}
                  autoHideDuration={3000}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <CssBaseline />
                  {/* <SnowParticles /> */}
                  <AnimatePresence exitBeforeEnter>
                    <motion.div transition={{ delay: 1 }}>
                      {loaded && <Component {...pageProps} shop={getShop()} />}
                    </motion.div>
                  </AnimatePresence>
                </SnackbarProvider>
              </ShopNavProvider>
            </ShopProvider>
          </GeolocationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </CacheProvider>
  );
};

MyApp.getInitialProps = async (appContext) => {
  let shop = { title: "Shop Not Found", description: "404 Not Found" };
  let validSubdomain = false;
  if (appContext.ctx.req) {
    try {
      const protocol = appContext.ctx.req.headers["x-forwarded-proto"] || "http";
      const origin = `${protocol}://${appContext.ctx.req.headers.host}`;
      const sanitizedOrigin = origin.replace(/\\/g, "");
      const res = await fetch(`${BACKEND_URL}/${API_VERSION}/shopfront`, {
        headers: { Origin: sanitizedOrigin, "Content-Type": "application/json" },
      });
      if (res.status === 200) {
        // valid subdomain
        shop = await res.json();
        validSubdomain = true;
      } else {
        throw Error;
      }
    } catch (err) {
      // invalid subdomain
      shop = { title: "Shop Not Found", description: "404 Not Found" };
      validSubdomain = false;
    }
  } else {
    // called on client side
    shop = JSON.parse(localStorage.getItem("shop"));
    validSubdomain = true;
  }

  const appProps = await App.getInitialProps(appContext);
  return { ...appProps, shop, validSubdomain };
};

export default MyApp;
